import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./AppNav.css";
// import Navbar from "./components/Navbar";
import DeveloperPanel from "./pages/DeveloperPanel";
import DashboardPage from "./pages/DashboardPage";
import DropshipEnterprisePanel from "./pages/DropshipEnterprisePanel";
import EnterprisesPage from "./pages/EnterprisesPage";
import SuppliersPage from "./pages/SuppliersPage";
import OrganizationPage from "./pages/OrganizationPage";
import PaymentReportsPage from "./pages/PaymentReportsPage";
import OrderReportsPage from "./pages/OrderReportsPage";
import OrderCorePage from "./pages/OrderCorePage";
import FulfillmentPage from "./pages/FulfillmentPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import DocumentsPage from "./pages/DocumentsPage";
import OwnWarehousePage from "./pages/OwnWarehousePage";
import CatalogPage from "./pages/CatalogPage";
import PurchasingPage from "./pages/PurchasingPage";

import Login from "./pages/Login";
import { clearAccessToken, restoreSession, setSessionExpiredHandler } from "./api/authSession";

const navItems = [
    { label: "Главная", path: "/dashboard" },
    { label: "Предприятия", path: "/enterprises" },
    { label: "Платежи", path: "/payment-reports" },
    { label: "Заказы", path: "/order-reports" },
    { label: "Склад", path: "/warehouse" },
    { label: "Каталог", path: "/catalog" },
    { label: "Закупки", path: "/purchasing" },
    { label: "Документы", path: "/documents" },
    { label: "Организация", path: "/organization" },
    { label: "Поставщики", path: "/suppliers" },
    { label: "Интеграции", path: "/integrations" },
    { label: "Админ", path: "/developer" },
];

const App = () => {
    const [authUser, setAuthUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const isActivePath = (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`);
    const loginElement = useMemo(() => {
        return authUser ? <Navigate to="/dashboard" replace /> : <Login setAuthUser={setAuthUser} />;
    }, [authUser]);

    useEffect(() => {
        let mounted = true;
        setSessionExpiredHandler(() => {
            clearAccessToken();
            if (mounted) setAuthUser(null);
        });
        restoreSession()
            .then((user) => {
                if (mounted) setAuthUser(user);
            })
            .catch(() => clearAccessToken())
            .finally(() => {
                if (mounted) setAuthLoading(false);
            });
        return () => {
            mounted = false;
            setSessionExpiredHandler(null);
        };
    }, []);

    if (authLoading) {
        return <div aria-live="polite" />;
    }

    const PrivateRoute = ({ element }) => {
        return authUser ? element : <Navigate to="/" />;
    };

    return (
        <div style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
            {authUser && (
                <div className="app-nav-wrap">
                    <nav className="app-nav" aria-label="Основная навигация">
                        {navItems.map((item) => {
                            const classes = ["n"];
                            if (item.accent) classes.push(item.accent);
                            if (isActivePath(item.path)) classes.push("active");
                            return (
                                <button
                                    key={item.path}
                                    type="button"
                                    className={classes.join(" ")}
                                    aria-current={isActivePath(item.path) ? "page" : undefined}
                                    onClick={() => navigate(item.path)}
                                >
                                    {item.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            )}
            <Routes>
                <Route path="/" element={loginElement} />
                <Route path="/login" element={loginElement} />
                <Route
                    path="/dashboard"
                    element={<PrivateRoute element={<DashboardPage authUser={authUser} />} />}
                />
                <Route
                    path="/developer"
                    element={<PrivateRoute element={<DeveloperPanel authUser={authUser} />} />}
                />
                <Route
                    path="/enterprises/*"
                    element={<PrivateRoute element={<EnterprisesPage authUser={authUser} />} />}
                />
                <Route path="/enterprise" element={<Navigate to="/enterprises/settings" replace />} />
                <Route
                    path="/suppliers"
                    element={<PrivateRoute element={<SuppliersPage authUser={authUser} />} />}
                />
                <Route
                    path="/dropship-enterprises"
                    element={<PrivateRoute element={<DropshipEnterprisePanel authUser={authUser} />} />}
                />
                <Route path="/mapping_branch" element={<Navigate to="/enterprises/branches" replace />} />
                <Route path="/formats" element={<Navigate to="/enterprises/formats" replace />} />
                <Route
                    path="/organization/*"
                    element={<PrivateRoute element={<OrganizationPage authUser={authUser} />} />}
                />
                <Route path="/store" element={<Navigate to="/organization/settings" replace />} />
                <Route path="/store/settings" element={<Navigate to="/organization/settings" replace />} />
                <Route path="/store/stores" element={<Navigate to="/organization/stores" replace />} />
                <Route path="/store/*" element={<Navigate to="/organization/settings" replace />} />
                <Route path="/business" element={<Navigate to="/organization/settings" replace />} />
                <Route path="/business-stores" element={<Navigate to="/organization/stores" replace />} />
                <Route path="/business-organizations" element={<Navigate to="/organization/legal-entities" replace />} />
                <Route
                    path="/integrations/*"
                    element={<PrivateRoute element={<IntegrationsPage authUser={authUser} />} />}
                />
                <Route
                    path="/payment-reports"
                    element={<PrivateRoute element={<PaymentReportsPage authUser={authUser} />} />}
                />
                <Route path="/finance-control" element={<Navigate to="/organization/finance-control" replace />} />
                <Route
                    path="/documents"
                    element={<PrivateRoute element={<DocumentsPage authUser={authUser} />} />}
                />
                <Route
                    path="/warehouse-stocks"
                    element={<Navigate to={{ pathname: "/warehouse/external-stocks", search: location.search, hash: location.hash }} replace />}
                />
                <Route
                    path="/warehouse/*"
                    element={<PrivateRoute element={<OwnWarehousePage authUser={authUser} />} />}
                />
                <Route
                    path="/catalog/*"
                    element={<PrivateRoute element={<CatalogPage authUser={authUser} />} />}
                />
                <Route
                    path="/purchasing/*"
                    element={<PrivateRoute element={<PurchasingPage authUser={authUser} />} />}
                />
                <Route
                    path="/order-reports"
                    element={<PrivateRoute element={<OrderReportsPage authUser={authUser} />} />}
                />
                {/* Служебная страница разбора контура заказов (Order Core, шаг S9a):
                    в меню намеренно не выводится — открывается по прямой ссылке
                    /order-core, когда нужно посмотреть судьбу конкретного заказа
                    или сверку агрегата с отчётностью. Для повседневной работы
                    предназначен раздел «Заказы» (/order-reports). */}
                <Route
                    path="/order-core"
                    element={<PrivateRoute element={<OrderCorePage authUser={authUser} />} />}
                />
                <Route
                    path="/fulfillment"
                    element={<PrivateRoute element={<FulfillmentPage authUser={authUser} />} />}
                />
            </Routes>
        </div>
    );
};

export default App;
